import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi, RCCSwitch } from "react-common-components";
import { useForm } from "react-hook-form";
import Audit from "src/components/Audit";
import DeleteModal from "src/components/DeleteModal";
import LovBusinessGroups from "src/components/Lov/BusinessGroups";
import LovHomePage from "src/components/Lov/HomePage";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import ScheduleRestriction from "src/components/ScheduleRestriction";
import { IRefSchedule } from "src/components/ScheduleRestriction/interface";
import Tabs from "src/components/Tabs";
import { useAuth } from "src/context/Auth/context";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { defaultStateSchedule } from "../Users/interface";
import { IComponentUserGroupModals, IEftba004, IRefUserGroupModals } from "./interface";
import { addAndChangeSchema, deleteSchema } from "./schemas";

const Modals: React.ForwardRefRenderFunction<IRefUserGroupModals, IComponentUserGroupModals> = ({
	paginationRef
}, ref) => {

	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const { businessGroup } = useAuth();

	const { setLoading } = useLoaderBackdrop();

	const modalDelete = useRef<RCCRefModalProps>(null);
	const refSchedule = useRef<IRefSchedule>(null);

	const { 
		handleSubmit, 
		register, 
		control, 
		reset, 
		setError, 
		formState: { errors }, 
		clearErrors
	} = useForm();

	const { 
		handleSubmit : handleSubmitDelete, 
		register : registerDelete, 
		reset : resetDelete, 
		setError : setErrorDelete, 
		formState: { errors : errorsDelete }, 
		clearErrors: clearErrorsDelete 
	} = useForm();

	const [isAdd, setIsAdd] = useState(false);
	const [selectedUserGroup, setSelectedUserGroup] = useState<IEftba004>({} as IEftba004);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);

	const queryData = async (id: number) => {
		setLoading(true);
		try{
			const { data } = await RCCServicesApi.getRequest({
				method: "UsersGroups/Select",
				urlParameters : {
					id
				}
			});
			setSelectedUserGroup(data);
			setLoading(false);
			modalAddOrChange.current?.openModal();
		}catch(err: any){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar dados"), addToast);
		}
	};

	const submitAddOrChange = async (data: any) => {
		try{
			clearErrors();
			modalAddOrChange.current?.setLoadingButton();
			
			await addAndChangeSchema(translate).validate({...data}, {
				abortEarly: false
			});

			const schedule = refSchedule.current?.getInnerData() ?? [];

			if(isAdd){
				await RCCServicesApi.postRequest({
					method: "UsersGroups/Insert",
					body: {
						...data,
						efa004Administrador: data.efa004Administrador ? "S" : "N",
						schedule: schedule.map((sched: any[]) => sched.map((s) => s.toString())),
						efa004Efa001Id: data.efa004Efa001Id !== "" ? data.efa004Efa001Id: null,
						efa004Paginicial: data.efa004Paginicial !== "" ? data.efa004Paginicial: null
					}
				});
			}else{
				await RCCServicesApi.putRequest({
					method: "UsersGroups/Update",
					body: {
						...data,
						efa004Id: selectedUserGroup.efa004Id,
						efa004Administrador: data.efa004Administrador ? "S" : "N",
						schedule: schedule.map((sched: any[]) => sched.map((s) => s.toString())),
						efa004Efa001Id: data.efa004Efa001Id !== "" ? data.efa004Efa001Id: null,
						efa004Paginicial: data.efa004Paginicial !== "" ? data.efa004Paginicial: null
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Grupo de usuários adicionado com sucesso") : translate("Grupo de usuários alterado com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
			paginationRef.current?.reload();

		}catch(err: any){

			modalAddOrChange.current?.resetLoadingButton();

			if(getValidationErrors(err, setError))
				return;

			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar grupo de usuários") : translate("Erro ao alterar grupo de usuários"), addToast);
		}
	};

	const submitDelete = async (data: any) => {
		try{	
			clearErrorsDelete();
			modalDelete.current?.setLoadingButton();

			await deleteSchema(translate).validate({...data, "efa004DescricaoConfirmation": selectedUserGroup.efa004Descricao},{
				abortEarly: false
			});

			await RCCServicesApi.deleteRequest({
				method: "UsersGroups/Delete",
				urlParameters: {
					id: selectedUserGroup.efa004Id
				}
			});

			addToast({
				color: "green",
				description: translate("Grupo de usuários excluído com sucesso"),
				title: translate("Sucesso")
			});
			modalDelete.current?.resetLoadingButton();
			modalDelete.current?.closeModal();
			paginationRef.current?.reload();

		}catch(err: any){
			modalDelete.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorDelete))
				return;

			const textMessageError = err?.response?.data?.Message;
			if(textMessageError === "hasUserRegistered"){
				addToast({
					title: translate("Erro"),
					description: translate("Este grupo de usuários possui usuários cadastrados"),
					color: "yellow"
				});
			}else{
				standardErrorMessages(err, translate("Erro ao excluir grupo de usuário"), addToast);
			}

		}	
	};
	
	const actionButtonAdd = () => {
		reset();
		setIsAdd(true);
		setSelectedUserGroup({
			schedule: defaultStateSchedule
		} as IEftba004);
		modalAddOrChange.current?.openModal();
	};

	const actionButtonChange = (row: IEftba004) => {
		reset();
		setIsAdd(false);
		queryData(row.efa004Id);
	};

	const actionButtonDelete = (row: IEftba004) => {
		resetDelete(); 
		setSelectedUserGroup(row); 
		modalDelete.current?.openModal();
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));
    
	return (
		<>
			<DeleteModal
				ref={modalDelete}
				title= {translate("Exclusão de grupo de usuários")}
				description= {`${translate("Deseja realmente excluir o grupo de usuários")}: ${selectedUserGroup.efa004Descricao}?`}
				onSubmit = {handleSubmitDelete(submitDelete)} 
				height="250px"
			>
				<RCCGrid
					container
					item
					xs={10}
				>
					<RCCInput 
						register={registerDelete}
						name="efa004Descricao"
						label={translate("Digite o grupo de usuários para excluí-lo")}
						maxLength={200}
						error={errorsDelete.efa004Descricao}
						required
					/>
				</RCCGrid>
			</DeleteModal>
    
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar Grupo de Usuários") : translate("Alterar Grupo de Usuários"),
					subtitle: !isAdd ? `${translate("Grupo de Usuários")}: ${selectedUserGroup.efa004Descricao}`: "",
					icon: {
						name: "users"
					}
				}}
				onSubmit={handleSubmit(submitAddOrChange)}
				styleInline={ModalSide}
			>
				<Tabs 
					name="tabsUserGroup"
					tabInit={0}
					options={[{
						label: translate("Geral"),
						content: <>
							<RCCGrid
								container
								item
								direction="column"
								alignItems="center"
							>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
								>
									<RCCGrid
										container 
										item
										xs={12}
										alignItems="center"
										justify="flex-end"
									>
										<Audit data={selectedUserGroup} type="pop"/>
									</RCCGrid>
									<RCCGrid container item>
										<RCCInput 
											register={register}
											name="efa004Descricao"
											label={translate("Descrição")}
											maxLength={200}
											defaultValue={selectedUserGroup.efa004Descricao ?? ""}
											error={errors.efa004Descricao}
											required
										/>
									</RCCGrid>

									{businessGroup.efa001Id === 1 && (
										<RCCGrid item container>
											<LovBusinessGroups 
												control={control}
												name="efa004Efa001Id" 
												defaultValue={selectedUserGroup.efa004Efa001Id ? selectedUserGroup.efa004Efa001Id.toString() : "1"}
												label={translate("Grupo de Empresas")}
												required
											/>
										</RCCGrid>
									)}

									<RCCGrid item container>
										<LovHomePage 
											control={control}
											name="efa004Paginicial" 
											defaultValue={selectedUserGroup.efa004Paginicial ? selectedUserGroup.efa004Paginicial.toString() : ""}
											submit="value"
											isClearable
										/>
									</RCCGrid>
									<RCCGrid item container>
										<RCCSwitch 
											name="efa004Administrador" 
											label={translate("Administrador")} 
											register={register} 
											defaultChecked={selectedUserGroup.efa004Administrador === "S"}
										/>
									</RCCGrid>	 
								</RCCGrid>
							</RCCGrid>
						</>
					},{
						label: translate("Restrição de horários"),
						content: <>
							<ScheduleRestriction ref={refSchedule} data={selectedUserGroup.schedule}/>
						</>
					}]}
				/>
			</Modal> 
		</>
	);
};


export default forwardRef(Modals);
